import React, { useState, useRef } from 'react';
import { UploadIcon, XIcon, FileIcon, AlertCircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIntake } from '../../context/IntakeContext';
const MAX_FILE_SIZE = 10 * 1024 * 1024;
interface FileUploadProps {
  label?: string;
  accept?: string;
  /** Preview-URL (alleen geldig in deze browser) */
  onChange: (fileUrl: string) => void;
  value?: string;
  helperText?: string;
  /** Sleutel waaronder het echte bestand wordt bewaard en als bijlage wordt meegestuurd */
  uploadKey?: string;
}
export function FileUpload({
  label,
  accept = 'image/*,.pdf,.doc,.docx',
  onChange,
  value,
  helperText,
  uploadKey
}: FileUploadProps) {
  const {
    uploadedFiles,
    setUploadedFile
  } = useIntake();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const storedFile = uploadKey ? uploadedFiles[uploadKey] : undefined;
  const handleFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setError(`"${file.name}" is ${formatSize(file.size)}. Bestanden mogen maximaal 10 MB zijn. Verklein het bestand of stuur het later na per e-mail.`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setError(null);
    if (value?.startsWith('blob:')) URL.revokeObjectURL(value);
    if (uploadKey) setUploadedFile(uploadKey, file);
    onChange(URL.createObjectURL(file));
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };
  const clearFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (value?.startsWith('blob:')) URL.revokeObjectURL(value);
    if (uploadKey) setUploadedFile(uploadKey, null);
    onChange('');
    if (inputRef.current) inputRef.current.value = '';
  };
  const openPicker = () => {
    if (!value) inputRef.current?.click();
  };
  const isImage = storedFile ? storedFile.type.startsWith('image/') : Boolean(value?.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i));
  return <div className="w-full" data-id="element-694" data-mp="920ptfsm2kr5">
      {label && <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-695" data-mp="93btlfm23si9">
          {label}
        </label>}

      <div role={value ? undefined : 'button'} tabIndex={value ? undefined : 0} onClick={openPicker} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openPicker();
      }
    }} onDragOver={e => {
      e.preventDefault();
      setIsDragging(true);
    }} onDragLeave={() => setIsDragging(false)} onDrop={handleDrop} className={`relative w-full rounded-xl border-2 border-dashed transition-colors duration-200 overflow-hidden ${error ? 'border-red-300 bg-red-50/40' : value ? 'border-gray-200 bg-gray-50' : isDragging ? 'border-[#007AFF] bg-[#F0F7FF]' : 'border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 cursor-pointer'}`} data-id="element-696" data-mp="11pwjebmvvwjp">
        <input type="file" ref={inputRef} onChange={handleFileChange} accept={accept} className="hidden" data-id="element-697" data-mp="26x656xe1mdup" />

        <AnimatePresence mode="wait" data-id="element-698" data-mp="2bvr4mulh0ti3">
          {value ? <motion.div key="filled" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="p-4 flex items-center justify-between" data-id="element-699" data-mp="2fx2n8g5xgegs">
              <div className="flex items-center space-x-4 overflow-hidden" data-id="element-700" data-mp="2una8t4r8edlp">
                <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-701" data-mp="2ukxjg6j590ym">
                  {isImage ? <img src={value} alt="" className="w-full h-full object-cover" data-id="element-702" data-mp="5reduunafib" /> : <FileIcon className="w-6 h-6 text-[#007AFF]" data-id="element-703" data-mp="9zqeypf2lr85" />}
                </div>
                <div className="min-w-0" data-id="element-704" data-mp="50nhpr5c0paq">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-705" data-mp="3ddkrpt9r23rq">
                    {storedFile?.name || 'Bestand geüpload'}
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-706" data-mp="38hm7mhvgf7x4">
                    {storedFile ? `${formatSize(storedFile.size)} · wordt als bijlage meegestuurd` : 'Klik op het kruisje om te verwijderen'}
                  </p>
                </div>
              </div>
              <button type="button" onClick={clearFile} aria-label="Bestand verwijderen" className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" data-id="element-707" data-mp="xn8b0x5dbbgx">
                <XIcon className="w-5 h-5" data-id="element-708" data-mp="1a75btd60lt0q" />
              </button>
            </motion.div> : <motion.div key="empty" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="p-8 text-center" data-id="element-709" data-mp="vao0glxtsee6">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#F0F7FF] flex items-center justify-center" data-id="element-710" data-mp="1sv0h08nk9yi8">
                <UploadIcon className="w-6 h-6 text-[#007AFF]" data-id="element-711" data-mp="1e6w63qur2ra0" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-712" data-mp="1j57io9cfjfvx">
                Klik of sleep een bestand hierheen
              </p>
              <p className="text-xs text-gray-500" data-id="element-713" data-mp="26u9x78brosm7">
                {helperText || 'Afbeeldingen en documenten, maximaal 10 MB'}
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>

      {error && <p role="alert" className="mt-2 flex items-start text-sm text-red-600" data-id="element-714" data-mp="2gjgvk5y8tuv5">
          <AlertCircleIcon className="w-4 h-4 mr-1.5 mt-0.5 flex-shrink-0" data-id="element-715" data-mp="2ch2cbkqnwt3c" />
          {error}
        </p>}
    </div>;
}
function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}